import axios, { AxiosError, type AxiosRequestConfig } from 'axios';
import { getInitDataRaw } from './telegram';

export interface ApiErrorBody {
  statusCode: number;
  code: string;
  message: string;
  details?: unknown;
  requestId?: string;
}

const REFRESH_KEY = 'cr:refresh';

export const api = axios.create({
  baseURL: import.meta.env['VITE_API_URL'] ?? '/api',
  timeout: 20_000,
});

/** Access-токен держим в памяти, refresh — в localStorage: WebView Telegram его сохраняет. */
let accessToken: string | null = null;

export function setTokens(access: string | null, refresh?: string | null): void {
  accessToken = access;

  if (refresh === null) localStorage.removeItem(REFRESH_KEY);
  else if (refresh) localStorage.setItem(REFRESH_KEY, refresh);
}

export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_KEY);
}

export function hasSession(): boolean {
  return Boolean(accessToken);
}

api.interceptors.request.use((config) => {
  if (accessToken) config.headers.Authorization = `Bearer ${accessToken}`;
  return config;
});

/** Обновляем токен один раз на волну параллельных 401 — иначе гонка ротаций. */
let refreshing: Promise<string> | null = null;

async function refreshSession(): Promise<string> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) throw new Error('Нет сохранённой сессии');

  refreshing ??= api
    .post<{ accessToken: string; refreshToken: string }>('/auth/refresh', { refreshToken })
    .then((res) => {
      setTokens(res.data.accessToken, res.data.refreshToken);
      return res.data.accessToken;
    })
    .finally(() => {
      refreshing = null;
    });

  return refreshing;
}

/** Обмен initData на пару токенов. Работает только внутри Telegram. */
export async function loginWithTelegram(): Promise<boolean> {
  const initData = getInitDataRaw();
  if (!initData) return false;

  const { data } = await api.post<{
    tokens: { accessToken: string; refreshToken: string };
  }>('/auth/telegram', { initData });

  setTokens(data.tokens.accessToken, data.tokens.refreshToken);
  return true;
}

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiErrorBody>) => {
    const config = error.config as (AxiosRequestConfig & { _retried?: boolean }) | undefined;
    const isAuthCall = config?.url?.startsWith('/auth/');

    if (error.response?.status === 401 && config && !config._retried && !isAuthCall) {
      config._retried = true;
      try {
        await refreshSession();
        return await api.request(config);
      } catch {
        // Сессия не восстановилась — пробуем зайти заново по initData
        try {
          if (await loginWithTelegram()) return await api.request(config);
        } catch {
          setTokens(null, null);
        }
      }
    }

    const body = error.response?.data;
    return Promise.reject(
      Object.assign(error, {
        apiCode: body?.code ?? 'NETWORK_ERROR',
        apiMessage: body?.message ?? 'Сервис недоступен',
      }),
    );
  },
);

export function errorMessage(error: unknown): string {
  if (error instanceof AxiosError) {
    return error.response?.data?.message ?? 'Сервис недоступен';
  }
  return error instanceof Error ? error.message : 'Неизвестная ошибка';
}
